import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, X, Check, LayoutGrid, ChevronLeft, ChevronRight, ArrowLeft, RotateCcw, Link2 } from 'lucide-react';
import { pyqAPI } from '../../services/api';
import api from '../../services/api';
import CommentSection from '../CommentSection/CommentSection';
import RichContent, { isHtml } from '../RichText/RichContent';
import ImportantTermText from '../ImportantTermText/ImportantTermText';
import PublicFooter from '../PublicFooter/PublicFooter';
import logoImg from '../../assets/logo.png';
import templeImg from '../../assets/temple-far1.jpg';
import './PYQSearchPage.css';

const ANSWERS_KEY = 'pyq_answers_v5';   // keeps existing progress
const SESSION_KEY = 'pyq_session_v5';

const SUGGESTIONS = ['Article 370', 'Monetary Policy', 'Paris Agreement', 'Panchayati Raj', 'GST', 'Biodiversity', 'PMLA', 'Fundamental Rights'];

const loadMap = key => {
  try { return JSON.parse(localStorage.getItem(key) || '{}') || {}; } catch { return {}; }
};
const save = (key, val) => {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch { /* storage unavailable */ }
};

function useDebounce(val, ms) {
  const [dv, setDv] = useState(val);
  useEffect(() => {
    const t = setTimeout(() => setDv(val), ms);
    return () => clearTimeout(t);
  }, [val, ms]);
  return dv;
}

/* ── Text helpers ─────────────────────────────────────── */
function splitIntroClosing(text) {
  if (!text) return { intro: '', closing: '' };
  const lines = text.split('\n').filter(l => l.trim());
  if (lines.length <= 1) return { intro: text, closing: '' };
  return { intro: lines[0], closing: lines.slice(1).join('\n') };
}

/* ── Table parsing: pipe tables, tab tables and "List I / List II" text become real tables ── */
const SEP = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/;
const LIST1 = /^\W*list\s*[-–—]?\s*(?:i|1)\b/i;
const LIST2 = /list\s*[-–—]?\s*(?:ii|2)\b/i;
const LEFT = /^\(?([A-Ea-e])[.)]\)?\s+(.+)$/;
const RIGHT = /^\(?([1-9])[.)]\)?\s+(.+)$/;
const cells = line => line.replace(/^\s*\|/, '').replace(/\|\s*$/, '').split('|').map(c => c.trim());

/* Finds a table starting at lines[i]: pipe tables, tab tables and "List I / List II" text */
function tryTable(lines, i) {
  const line = lines[i].trim();
  const next = (lines[i + 1] || '').trim();

  if (line.includes('|') && cells(line).length >= 2) {
    if (SEP.test(next)) {
      const rows = [];
      let j = i + 2;
      while (j < lines.length && lines[j].includes('|')) { rows.push(cells(lines[j])); j += 1; }
      return { head: cells(line), rows, next: j };
    }
    if (next.includes('|') && cells(next).length >= 2) {
      const all = [];
      let j = i;
      while (j < lines.length && lines[j].includes('|') && !SEP.test(lines[j].trim())) { all.push(cells(lines[j])); j += 1; }
      if (all.length >= 2) return { head: all[0], rows: all.slice(1), next: j };
    }
  }

  if (line.includes('\t') && next.includes('\t')) {
    const all = [];
    let j = i;
    while (j < lines.length && lines[j].includes('\t')) { all.push(lines[j].trim().split(/\t+/)); j += 1; }
    return { head: all[0], rows: all.slice(1), next: j };
  }

  if (LIST1.test(line)) {
    if (LIST2.test(line)) {
      const head = [line.slice(0, line.search(LIST2)).trim(), line.slice(line.search(LIST2)).trim()];
      const rows = [];
      const lefts = [];
      const rights = [];
      let j = i + 1;
      while (j < lines.length) {
        const t = lines[j].trim();
        if (!t) break;
        const parts = t.split(/\t+|\s{2,}/);
        const both = t.match(/^\(?([A-Ea-e])[.)]\)?\s+(.+?)\s+\(?([1-9])[.)]\)?\s+(.+)$/);
        if (parts.length >= 2 && LEFT.test(parts[0]) && RIGHT.test(parts[1])) rows.push([parts[0], parts[1]]);
        else if (both) rows.push([`${both[1]}. ${both[2]}`, `${both[3]}. ${both[4]}`]);
        else if (LEFT.test(t) && !rights.length) lefts.push(t);
        else if (RIGHT.test(t)) rights.push(t);
        else break;
        j += 1;
      }
      for (let r = 0; r < Math.max(lefts.length, rights.length); r += 1) rows.push([lefts[r] || '', rights[r] || '']);
      if (rows.length) return { head, rows, next: j };
    } else {
      const lefts = [];
      let j = i + 1;
      while (j < lines.length && LEFT.test(lines[j].trim())) { lefts.push(lines[j].trim()); j += 1; }
      if (lefts.length && j < lines.length && LIST2.test(lines[j].trim())) {
        const head = [line, lines[j].trim()];
        const rights = [];
        j += 1;
        while (j < lines.length && RIGHT.test(lines[j].trim())) { rights.push(lines[j].trim()); j += 1; }
        if (rights.length) return { head, rows: lefts.map((l, r) => [l, rights[r] || '']), next: j };
      }
    }
  }
  return null;
}

function TableBlock({ t, disableLinks = false }) {
  return (
    <div className="pq-match">
      <table>
        <thead><tr>{t.head.map((h, i) => <th key={i}><ImportantTermText text={h} disableLinks={disableLinks} /></th>)}</tr></thead>
        <tbody>{t.rows.map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j}><ImportantTermText text={c} disableLinks={disableLinks} /></td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}

function FormattedText({ text }) {
  if (!text) return null;
  if (isHtml(text)) return <div className="pq-rich"><RichContent html={text} /></div>;
  const lines = text.split('\n');
  const out = [];
  let skipTermLinks = false;
  for (let i = 0; i < lines.length;) {
    const t = lines[i].trim();
    if (!t) { out.push(<div key={i} className="pq-gap" />); i += 1; continue; }
    if (/^For Interested Students\b/i.test(t)) skipTermLinks = true;
    const tb = tryTable(lines, i);
    if (tb) { out.push(<TableBlock key={i} t={tb} disableLinks={skipTermLinks} />); i = tb.next; continue; }
    const num = t.match(/^(\d+)[.)]\s/);
    const bullet = /^[•\-*→]\s/.test(t);
    const clean = t.replace(/^\d+[.)]\s*|^[•\-*→]\s*|\*\*/g, '').trim();
    if (num) out.push(<div key={i} className="pq-li"><b>{num[1]}.</b><span><ImportantTermText text={clean} disableLinks={skipTermLinks} /></span></div>);
    else if (bullet) out.push(<div key={i} className="pq-li"><b>•</b><span><ImportantTermText text={clean} disableLinks={skipTermLinks} /></span></div>);
    else out.push(<p key={i}><ImportantTermText text={t} disableLinks={skipTermLinks} /></p>);
    i += 1;
  }
  return <div className="pq-text">{out}</div>;
}

function FormattedQuestion({ text }) {
  if (!text) return null;
  if (isHtml(text)) return <div className="pq-qtext pq-rich"><RichContent html={text} /></div>;
  const lines = text.split('\n');
  const out = [];
  for (let i = 0; i < lines.length;) {
    const t = lines[i].trim();
    if (!t) { i += 1; continue; }
    const tb = tryTable(lines, i);
    if (tb) { out.push(<TableBlock key={i} t={tb} />); i = tb.next; continue; }
    const num = t.match(/^(\d+)[.)]\s/);
    const clean = t.replace(/^\d+[.)]\s*|\*\*/g, '').trim();
    if (num) out.push(<div key={i} className="pq-li"><b>{num[1]}.</b><span><ImportantTermText text={clean} /></span></div>);
    else out.push(<p key={i} className={/^[A-Z][^a-z]{4,}/.test(t) ? 'pq-strong' : ''}><ImportantTermText text={t.replace(/\*\*/g, '')} /></p>);
    i += 1;
  }
  return <div className="pq-qtext">{out}</div>;
}

function MatchLists({ matchLists }) {
  if (!matchLists) return null;
  const { list1Label = 'List I', list2Label = 'List II', list1Items = [], list2Items = [] } = matchLists;

  const normalize = (items, numeric) => (Array.isArray(items) ? items : [])
    .map((item, i) => ({
      key: typeof item === 'string' ? (numeric ? String(i + 1) : String.fromCharCode(65 + i)) : String(item?.key || (numeric ? i + 1 : String.fromCharCode(65 + i))),
      text: typeof item === 'string' ? item : (typeof item?.text === 'string' ? item.text : ''),
    }))
    .filter(item => item.text.trim());

  const left = normalize(list1Items, false);
  const right = normalize(list2Items, true);
  if (!left.length && !right.length) return null;

  return (
    <div className="pq-match">
      <table aria-label={`${list1Label} and ${list2Label}`}>
        <thead><tr><th>{list1Label}</th><th>{list2Label}</th></tr></thead>
        <tbody>
          {Array.from({ length: Math.max(left.length, right.length) }, (_, i) => (
            <tr key={i}>
              <td>{left[i] && <><b>{left[i].key}.</b> <ImportantTermText text={left[i].text} /></>}</td>
              <td>{right[i] && <><b>{right[i].key}.</b> <ImportantTermText text={right[i].text} /></>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ── URL helpers (a question's permanent page is /pyq/:id) ── */
const getQuestionIdFromPath = () => {
  if (typeof window === 'undefined') return null;
  const m = window.location.pathname.match(/\/pyq\/([a-f\d]{24})\/?$/i);
  return m ? m[1] : null;
};
const publicQuestionUrl = q => (q?._id ? `/pyq-question/${encodeURIComponent(q.slug || q._id)}` : null);

/* ═════════════════════════════════════════════════════════
   PAGE
   ═════════════════════════════════════════════════════════ */
export default function PYQSearchPage() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailId, setDetailId] = useState(() => getQuestionIdFromPath());
  const [detailLoading, setDetailLoading] = useState(() => Boolean(getQuestionIdFromPath()));
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [resume, setResume] = useState(null);
  const [answers, setAnswers] = useState(() => loadMap(ANSWERS_KEY));
  const [copied, setCopied] = useState(false);

  const isLoggedIn = !!localStorage.getItem('vidyacards_token');
  const debQuery = useDebounce(query, 350);
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  const isDetailPage = Boolean(detailId);
  const hasQuery = isDetailPage || !!query.trim();
  const pending = isDetailPage ? detailLoading : (hasQuery && (loading || debQuery !== query));

  /* This page is full screen, so lock the page behind it */
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prev = [html.style.overflow, body.style.overflow];
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    return () => { html.style.overflow = prev[0]; body.style.overflow = prev[1]; };
  }, []);

  useEffect(() => {
    try {
      const s = JSON.parse(localStorage.getItem(SESSION_KEY));
      if (s?.query) setResume(s);
    } catch { /* ignore malformed session */ }
  }, []);

  const fetchResults = useCallback(async q => {
    if (!q.trim()) { setResults([]); return; }
    setLoading(true);
    try {
      const res = await pyqAPI.search(q);
      setResults(res.data.results || []);
      setSelectedIdx(0);
    } catch (err) { console.error(err); setResults([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (isDetailPage) return;
    fetchResults(debQuery);
  }, [debQuery, fetchResults, isDetailPage]);

  /* Direct question page */
  useEffect(() => {
    if (!detailId) { setDetailLoading(false); return undefined; }
    let cancelled = false;
    setDetailLoading(true);
    api.get(`/admin/public-exam-questions/${detailId}`)
      .then(res => {
        if (cancelled) return;
        const q = res.data?.question;
        setResults(q ? [q] : []);
        setSelectedIdx(0);
      })
      .catch(err => { if (!cancelled) { console.error('Failed to load public PYQ', err); setResults([]); } })
      .finally(() => { if (!cancelled) setDetailLoading(false); });
    return () => { cancelled = true; };
  }, [detailId]);

  useEffect(() => {
    const onPop = () => setDetailId(getQuestionIdFromPath());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    if (!isDetailPage && debQuery) save(SESSION_KEY, { query: debQuery, selectedIdx });
  }, [debQuery, selectedIdx, isDetailPage]);

  useEffect(() => { save(ANSWERS_KEY, answers); }, [answers]);

  /* Group by subject for the palette */
  const { flat, groups } = useMemo(() => {
    const by = new Map();
    results.forEach(r => {
      const subject = (r.subject || '').trim() || 'General';
      if (!by.has(subject)) by.set(subject, []);
      by.get(subject).push(r);
    });
    const flatList = [];
    const groupList = [];
    by.forEach((items, subject) => {
      const tiles = items.map(q => {
        const idx = flatList.length;
        flatList.push({ ...q, _k: q._id ?? `q${idx}`, _subject: subject });
        return idx;
      });
      groupList.push({ subject, tiles });
    });
    return { flat: flatList, groups: groupList };
  }, [results]);

  const card = hasQuery ? flat[selectedIdx] : undefined;
  const isMains = card?.questionType === 'mains';
  const selectedOpt = card ? answers[card._k] : undefined;
  const answered = selectedOpt !== undefined;
  const mainsAnswer = card?.answer || card?.modelAnswer || (isMains ? card?.explanation : '');
  const isMatch = card?.questionType === 'match-list';
  const lists = card?.matchLists;
  const hasLists = !!lists && ((lists.list1Items || []).length > 0 || (lists.list2Items || []).length > 0);
  const isAR = card?.questionType === 'assertion-reason';
  const isStatement = card?.questionType === 'statement' && card?.statements?.length > 0;
  const { intro: stmtIntro, closing: stmtClosing } = isStatement ? splitIntroClosing(card.question) : { intro: '', closing: '' };
  const hasPalette = hasQuery && flat.length > 0;

  /* SEO for question pages */
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const head = document.head;
    const meta = (sel, attrs, content) => {
      let el = head.querySelector(sel);
      if (!el) { el = document.createElement('meta'); Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v)); head.appendChild(el); }
      el.setAttribute('content', content);
    };
    let canonical = head.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); canonical.setAttribute('rel', 'canonical'); head.appendChild(canonical); }
    const ld = document.getElementById('pyq-structured-data');

    if (!isDetailPage) {
      const title = 'Search UPSC PYQs, State PCS & SSC Questions | CivilFox';
      const description = 'Search previous-year questions for UPSC, State PCS, SSC and other competitive exams where available. Find questions by keyword, subject, exam and year, then practise with answers and explanations.';
      document.title = title;
      meta('meta[name="description"]', { name: 'description' }, description);
      meta('meta[property="og:title"]', { property: 'og:title' }, title);
      meta('meta[property="og:description"]', { property: 'og:description' }, description);
      meta('meta[property="og:url"]', { property: 'og:url' }, `${window.location.origin}/pyq-search`);
      meta('meta[name="robots"]', { name: 'robots' }, 'index,follow');
      canonical.setAttribute('href', `${window.location.origin}/pyq-search`);
      if (ld) ld.remove();
      return;
    }
    if (!card?._id) return;

    const plain = String(card.question || '').replace(/\s+/g, ' ').trim();
    const title = `${plain.slice(0, 150)}${plain.length > 150 ? '…' : ''} | CivilFox PYQ Bank`;
    const description = `${card.exam || 'Previous Year Question'}${card.year ? ` ${card.year}` : ''} · ${card.subject || 'General'}: ${plain}`.slice(0, 300);
    const url = `${window.location.origin}${publicQuestionUrl(card)}`;

    document.title = title;
    meta('meta[name="description"]', { name: 'description' }, description);
    meta('meta[property="og:title"]', { property: 'og:title' }, title);
    meta('meta[property="og:description"]', { property: 'og:description' }, description);
    meta('meta[name="robots"]', { name: 'robots' }, 'index,follow');
    canonical.setAttribute('href', url);

    let jsonLd = document.getElementById('pyq-structured-data');
    if (!jsonLd) { jsonLd = document.createElement('script'); jsonLd.id = 'pyq-structured-data'; jsonLd.type = 'application/ld+json'; head.appendChild(jsonLd); }
    jsonLd.textContent = JSON.stringify({
      '@context': 'https://schema.org', '@type': 'WebPage', name: title, description, url,
      about: [card.subject, card.topic, card.exam].filter(Boolean),
      dateModified: card.updatedAt || card.createdAt || undefined,
    });
  }, [isDetailPage, card?._id, card?.question, card?.exam, card?.year, card?.subject, card?.topic, card?.updatedAt, card?.createdAt]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { rootRef.current?.scrollTo?.(0, 0); }, [selectedIdx, flat]);

  const goPrev = () => setSelectedIdx(i => Math.max(0, i - 1));
  const goNext = () => setSelectedIdx(i => Math.min(flat.length - 1, i + 1));
  const goTile = idx => { setSelectedIdx(idx); setPaletteOpen(false); };

  const answerQuestion = useCallback(i => {
    if (!card || answers[card._k] !== undefined) return;
    setAnswers(a => ({ ...a, [card._k]: i }));
  }, [card, answers]);

  const tryAgain = () => {
    if (!card) return;
    setAnswers(a => { const n = { ...a }; delete n[card._k]; return n; });
  };

  const goBack = () => {
    if (window.history.length > 1) window.history.back();
    else window.location.assign('/');
  };

  const resetToSearch = () => {
    setDetailId(null);
    if (getQuestionIdFromPath()) window.history.replaceState({}, '', '/pyq-search');
  };

  const share = async () => {
    const url = `${window.location.origin}${publicQuestionUrl(card)}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { window.prompt('Copy link', url); }
  };

  /* Keys: arrows move, A-D answer, / searches, Esc closes the palette */
  useEffect(() => {
    const onKey = e => {
      if (e.key === 'Escape') { setPaletteOpen(false); return; }
      const t = e.target;
      const typing = t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable);
      if (typing || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'ArrowRight') { e.preventDefault(); goNext(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); goPrev(); }
      else if (e.key === '/') { e.preventDefault(); inputRef.current?.focus(); }
      else if (e.key.length === 1) {
        const n = 'abcd'.indexOf(e.key.toLowerCase());
        if (n > -1 && card?.options?.[n] !== undefined) answerQuestion(n);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const tileState = (q, idx) => {
    if (idx === selectedIdx) return 'cur';
    const a = answers[q._k];
    if (a === undefined) return '';
    return a === q.correctAnswer ? 'right' : 'wrong';
  };

  const questionBlock = () => {
    if (isMatch || hasLists) return (
      <>
        {card.matchIntro && <p className="pq-intro">{card.matchIntro}</p>}
        <MatchLists matchLists={card.matchLists} />
        <FormattedQuestion text={card.question} />
      </>
    );
    if (isAR) return (
      <>
        {card.assertion && (
          <div className="pq-ar">
            <p><b>Assertion (A):</b> {card.assertion}</p>
            <p><b>Reason (R):</b> {card.reason}</p>
          </div>
        )}
        <FormattedQuestion text={card.question} />
      </>
    );
    if (isStatement) return (
      <>
        <FormattedQuestion text={stmtIntro} />
        <ol className="pq-stmts">{card.statements.map((s, i) => <li key={i}>{s}</li>)}</ol>
        {stmtClosing && <FormattedQuestion text={stmtClosing} />}
      </>
    );
    return <FormattedQuestion text={card.question} />;
  };

  return (
    <div className="pq">
      <div className="pq-shell">
        {/* Left: subject palette */}
        <aside className={`pq-side ${paletteOpen ? 'open' : ''}`} aria-label="Questions">
          <img className="pq-side-art" src={templeImg} alt="" />
          <div className="pq-side-body">
            <div className="pq-side-top">
              <Link to="/" className="pq-logo" aria-label="CivilFox home"><img src={logoImg} alt="" /></Link>
              <button type="button" className="pq-x" onClick={() => setPaletteOpen(false)} aria-label="Close"><X size={18} /></button>
            </div>

            <nav className="pq-subjects">
              {groups.map(g => (
                <section key={g.subject} className={g.tiles.includes(selectedIdx) ? 'on' : ''}>
                  <h2><i className="pq-dot" />{g.subject}</h2>
                  <div className="pq-tiles">
                    {g.tiles.map((idx, n) => (
                      <button key={flat[idx]._k} type="button" className={`pq-tile ${tileState(flat[idx], idx)}`}
                        aria-label={`${g.subject} ${n + 1}`} aria-current={idx === selectedIdx ? 'true' : undefined}
                        onClick={() => goTile(idx)}>{n + 1}</button>
                    ))}
                  </div>
                </section>
              ))}
            </nav>

            <button type="button" className="pq-back" onClick={goBack}><ArrowLeft size={18} />Go back</button>
          </div>
        </aside>

        {/* Right: search and question */}
        <div className="pq-content" ref={rootRef}>
          <div className="pq-bar">
            {hasPalette && (
              <button type="button" className="pq-grid-btn" onClick={() => setPaletteOpen(true)} aria-label="All questions">
                <LayoutGrid size={18} />
              </button>
            )}
            <label className="pq-search">
              <Search size={22} />
              <input
                ref={inputRef}
                type="text"
                enterKeyHint="search"
                placeholder="Search"
                aria-label="Search previous year questions"
                autoComplete="off"
                value={query}
                onChange={e => { if (detailId || getQuestionIdFromPath()) resetToSearch(); setQuery(e.target.value); }}
              />
              {query && (
                <button type="button" className="pq-clear" aria-label="Clear search"
                  onClick={() => { setQuery(''); setResults([]); resetToSearch(); inputRef.current?.focus(); }}>
                  <X size={16} />
                </button>
              )}
            </label>
          </div>

          <main className="pq-main">
            {!hasQuery ? (
              <>
                <div className="pq-intro-copy">
                  <h1>Search UPSC and competitive exam PYQs</h1>
                  <p>
                    Find previous-year questions by keyword and subject. Search UPSC, State PCS,
                    SSC and other exam questions available in the CivilFox question bank.
                  </p>
                </div>
                <div className="pq-start">
                {resume && <button type="button" className="pq-chip hot" onClick={() => { setQuery(resume.query); setResume(null); }}>{resume.query}</button>}
                {SUGGESTIONS.filter(s => s !== resume?.query).map(s => (
                  <button key={s} type="button" className="pq-chip" onClick={() => setQuery(s)}>{s}</button>
                ))}
                </div>
              </>
            ) : card ? (
              <article>
                <section className="pq-card">
                <div className="pq-meta">
                  <div className="pq-tags">
                    {card.exam && <span>{card.exam}</span>}
                    {card.year && <span>{card.year}</span>}
                    <span>{card.subject || card._subject}</span>
                  </div>
                  {card._id && (
                    <button type="button" className="pq-ibtn" onClick={share} aria-label="Copy link">
                      {copied ? <Check size={15} /> : <Link2 size={15} />}
                    </button>
                  )}
                </div>

                <div className="pq-q">{questionBlock()}</div>

                {!isMains && card.options?.length > 0 && (
                  <div className="pq-opts" role="group" aria-label="Options">
                    {card.options.map((opt, i) => {
                      let st = '';
                      if (answered) st = i === card.correctAnswer ? 'ok' : i === selectedOpt ? 'bad' : 'dim';
                      return (
                        <button key={i} type="button" className={`pq-opt ${st} ${answered ? 'locked' : ''}`}
                          aria-pressed={i === selectedOpt} onClick={() => answerQuestion(i)}>
                          <b>{String.fromCharCode(65 + i)}</b>
                          <span>{opt}</span>
                          {st === 'ok' && <Check size={17} role="img" aria-label="Correct" />}
                          {st === 'bad' && <X size={17} role="img" aria-label="Wrong" />}
                        </button>
                      );
                    })}
                  </div>
                )}

                </section>

                {isMains && (
                  <div className="pq-a">
                    {mainsAnswer ? <FormattedText text={mainsAnswer} /> : <p className="pq-muted">Answer not available yet.</p>}
                  </div>
                )}
                {!isMains && answered && card.explanation && (
                  <div className="pq-a"><FormattedText text={card.explanation} /></div>
                )}

                {(flat.length > 1 || answered) && (
                  <div className="pq-nav">
                    {flat.length > 1 && (
                      <>
                        <button type="button" className="pq-nbtn" onClick={goPrev} disabled={selectedIdx === 0} aria-label="Previous"><ChevronLeft size={18} /></button>
                        <span className="pq-pos">{selectedIdx + 1}/{flat.length}</span>
                        <button type="button" className="pq-nbtn" onClick={goNext} disabled={selectedIdx === flat.length - 1} aria-label="Next"><ChevronRight size={18} /></button>
                      </>
                    )}
                    {answered && (
                      <button type="button" className="pq-nbtn ghost" onClick={tryAgain} aria-label="Try again"><RotateCcw size={16} /></button>
                    )}
                  </div>
                )}

                {card._id && (
                  <div className="pq-comments"><CommentSection cardId={card._id} isLoggedIn={isLoggedIn} /></div>
                )}
              </article>
            ) : pending ? (
              <div className="pq-state"><span className="pq-spin" role="status" aria-label="Loading" /></div>
            ) : (
              <p className="pq-state pq-muted">{isDetailPage ? 'Question not found.' : 'No results.'}</p>
            )}
          </main>
          <PublicFooter />
        </div>
      </div>

      <div className={`pq-backdrop ${paletteOpen ? 'show' : ''}`} onClick={() => setPaletteOpen(false)} />
    </div>
  );
}